import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { downloadQuotationPdf } from "@/lib/quotationPdf";

export const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Sent: "bg-sky-50 text-sky-700 border-sky-200",
  Opened: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Expired: "bg-amber-50 text-amber-700 border-amber-200",
  Superseded: "bg-zinc-100 text-zinc-500 border-zinc-300 line-through decoration-zinc-400",
};

export const RevBadge = ({ revision, testId }) => (revision > 0 ? (
  <span className="inline-flex items-center rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700" data-testid={testId}>
    Rev {revision}
  </span>
) : null);

export const isRentalQuote = (q) => q.quote_type === "rental" || q.quote_type === "rental_buy";
// Monthly rent is never added to sale totals.
export const saleValue = (q) => (isRentalQuote(q) ? 0 : Number(q.total_amount) || 0);

export const QuoteAmount = ({ q, fmt, testId }) => (
  <span data-testid={testId}>
    {fmt(q.total_amount)}{isRentalQuote(q) && <span className="text-[11px] font-normal text-slate-500"> / month</span>}
    {(isRentalQuote(q) || (q.additional_options || []).length > 0) && (
      <span className="block text-[10px] font-medium text-sky-700">
        {isRentalQuote(q) ? (q.quote_type === "rental_buy" ? "Rental · buy option" : "Rental") : "Sale"}
        {(q.additional_options || []).length > 0 ? ` · ${(q.additional_options || []).length + 1} options` : ""}
      </span>
    )}
  </span>
);


export function DownloadPdfButton({ quotation, compact = false, testId, className = "" }) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setBusy(true);
    try {
      const res = await downloadQuotationPdf(quotation);
      if (res === "downloaded") toast.success(`Downloaded ${quotation.pdf_filename}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const Icon = busy ? Loader2 : Download;
  if (compact) {
    return (
      <button type="button" onClick={run} disabled={busy} title="Download PDF" aria-label="Download PDF"
        className={`p-1.5 rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors ${className}`} data-testid={testId}>
        <Icon className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
      </button>
    );
  }
  return (
    <Button variant="outline" onClick={run} disabled={busy} className={className} data-testid={testId}>
      <Icon className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Download PDF
    </Button>
  );
}
